import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from 'react';
import { API_URL } from '../config/env';
import { useAccountData } from './AccountContext';

export const STORAGE_KEY = 'mgv.routePlanner.v1';

export const initialState = { itinerary: [], bookings: [] };

function entry(route, config, priceUsd, status) {
  return {
    routeId: route.id,
    name: route.name,
    people: config.people,
    tier: config.tier,
    inclusions: { ...config.inclusions },
    priceUsd,
    status: status || 'planned',
    addedAt: new Date().toISOString(),
  };
}

export function routePlannerReducer(state, action) {
  switch (action.type) {
    case 'add':
      return { ...state, itinerary: [...state.itinerary, entry(action.route, action.config, action.priceUsd)] };
    case 'remove':
      return { ...state, itinerary: state.itinerary.filter((e) => e.routeId !== action.routeId) };
    case 'book':
      return {
        itinerary: state.itinerary.filter((e) => e.routeId !== action.route.id),
        bookings: [...state.bookings, entry(action.route, action.config, action.priceUsd, 'pending')],
      };
    case 'bookAll':
      return {
        itinerary: [],
        bookings: [
          ...state.bookings,
          ...state.itinerary.map((e) => ({ ...e, status: 'pending' })),
        ],
      };
    case 'clear':
      return { ...state, itinerary: [] };
    case 'hydrate':
      return action.state || initialState;
    default:
      return state;
  }
}

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return initialState;
    const parsed = JSON.parse(raw);
    return {
      itinerary: Array.isArray(parsed.itinerary) ? parsed.itinerary : [],
      bookings: Array.isArray(parsed.bookings) ? parsed.bookings : [],
    };
  } catch {
    return initialState;
  }
}

const RoutePlannerContext = createContext(null);

export function RoutePlannerProvider({ children }) {
  const [state, dispatch] = useReducer(routePlannerReducer, initialState, loadState);
  const { token } = useAccountData() || {};

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* quota — ignore */ }
  }, [state]);

  // Best-effort server mirror for signed-in users. Non-fatal: the route planner
  // works fully offline from localStorage if the namespace is not deployed.
  useEffect(() => {
    if (!token) return undefined;
    const controller = new AbortController();
    fetch(`${API_URL}/user/data/routePlanner`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ value: state }),
      signal: controller.signal,
    }).catch(() => {});
    return () => controller.abort();
  }, [state, token]);

  const addToItinerary = useCallback((route, config, priceUsd) => dispatch({ type: 'add', route, config, priceUsd }), []);
  const removeFromItinerary = useCallback((routeId) => dispatch({ type: 'remove', routeId }), []);
  const bookRoute = useCallback((route, config, priceUsd) => dispatch({ type: 'book', route, config, priceUsd }), []);
  const bookAllItinerary = useCallback(() => dispatch({ type: 'bookAll' }), []);
  const clearItinerary = useCallback(() => dispatch({ type: 'clear' }), []);

  const value = useMemo(
    () => ({ itinerary: state.itinerary, bookings: state.bookings, addToItinerary, removeFromItinerary, bookRoute, bookAllItinerary, clearItinerary }),
    [state, addToItinerary, removeFromItinerary, bookRoute, bookAllItinerary, clearItinerary],
  );

  return <RoutePlannerContext.Provider value={value}>{children}</RoutePlannerContext.Provider>;
}

export function useRoutePlanner() {
  const ctx = useContext(RoutePlannerContext);
  if (!ctx) throw new Error('useRoutePlanner must be used within a RoutePlannerProvider');
  return ctx;
}
